import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Chart as ChartJS, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Tooltip as PrTooltip } from 'primereact/tooltip';
import './billreports.css';

ChartJS.register(DoughnutController, ArcElement, Tooltip, Legend);

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
    });
};

const defaultFormatLabel = (key) => {
    if (!key) return '';
    return key
        .replace(/^sum_/i, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

const defaultIsCurrency = (key) => {
    const k = (key || '').toLowerCase();
    return k.includes('value') || k.includes('revenue') || k.includes('taxable') || k.includes('tax') || k.includes('discount') || k.includes('amt') || k.includes('total');
};

const defaultGetCardStyle = (key) => {
    const k = (key || '').toLowerCase();
    const isHighlight = k.includes('net_revenue') || k.includes('sum_net') || k.includes('total') || k.includes('revenue') || k.includes('actual') || k.includes('expected');
    const styles = {
        nc_qty: { bg: 'linear-gradient(135deg, #e34948 0%, #b91c1c 100%)', icon: 'pi-tag' },
        nc_value: { bg: 'linear-gradient(135deg, #e34948 0%, #b91c1c 100%)', icon: 'pi-tag' },
        discounted: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-percentage' },
        discount: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-percentage' },
        sales_qty: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-box' },
        qty_all: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-box' },
        quantity: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-box' },
        taxable: { bg: 'linear-gradient(135deg, #1baf7a 0%, #0f766e 100%)', icon: 'pi-wallet' },
        tax: { bg: 'linear-gradient(135deg, #4a3aa7 0%, #372a7a 100%)', icon: 'pi-dollar' },
        net_revenue: { bg: 'linear-gradient(135deg, #4a3aa7 0%, #372a7a 100%)', icon: 'pi-chart-line' },
        total: { bg: 'linear-gradient(135deg, #4a3aa7 0%, #372a7a 100%)', icon: 'pi-chart-line' },
        cash: { bg: 'linear-gradient(135deg, #1baf7a 0%, #0f766e 100%)', icon: 'pi-wallet' },
        card: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-credit-card' },
        actual: { bg: 'linear-gradient(135deg, #eb6834 0%, #c2410c 100%)', icon: 'pi-chart-bar' },
        expected: { bg: 'linear-gradient(135deg, #4a3aa7 0%, #372a7a 100%)', icon: 'pi-chart-line' }
    };
    const fallbackPalette = [
        { bg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', icon: 'pi-wallet' },
        { bg: 'linear-gradient(135deg, #e87ba4 0%, #be3a72 100%)', icon: 'pi-chart-bar' },
        { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', icon: 'pi-box' }
    ];
    for (const [match, style] of Object.entries(styles)) {
        if (k.includes(match)) return { bg: style.bg, icon: style.icon, isHighlight };
    }
    const hash = (key || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const fallback = fallbackPalette[Math.abs(hash) % fallbackPalette.length];
    return { bg: fallback.bg, icon: fallback.icon, isHighlight };
};

const summaryNumericValue = (key, value) => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    const str = String(value).replace(/[₹,\s]/g, '');
    const num = parseFloat(str);
    return Number.isNaN(num) ? 0 : num;
};

// Pulls the first hex out of a `linear-gradient(...)` string so the same accent
// color can drive the tile icon, its border, and the matching donut slice.
const extractAccentHex = (bg) => {
    const m = String(bg || '').match(/#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})/);
    return m ? `#${m[1]}` : '#4a3aa7';
};

const hexToRgba = (hex, alpha) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function ReportSummaryWithChart({
    summary,
    fromDate,
    toDate,
    selectedBranch,
    title = 'Summary',
    subtitle,
    getCardStyle = defaultGetCardStyle,
    formatLabel = defaultFormatLabel,
    isCurrency = defaultIsCurrency,
    excludeFromChart = () => false,
    emphasizeKey = () => false,
    alwaysShowKey = () => false,
    getTooltip = () => null
}) {
    const summaryEntries = useMemo(() => Object.entries(summary || {}).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== ''), [summary]);

    // Hero tiles (grand totals) — always shown, even at zero.
    const highlightEntries = useMemo(() => summaryEntries.filter(([key]) => getCardStyle(key).isHighlight), [summaryEntries, getCardStyle]);

    // Breakdown rows (payment modes, categories, etc.) — a zero-value row (e.g. an
    // unused payment mode) adds nothing to a "how did sales split" view, so it's hidden,
    // *unless* the caller flags it via alwaysShowKey (e.g. a fixed structural total like
    // NC/Expected/Cancelled that should stay visible even when it happens to be zero for
    // the selected range — it's still a defined column in the table, not an optional mode).
    // These render in the same order they were given in `summary`, so a caller that
    // wants e.g. "payment modes, then Actual/Expected/Total" just orders its object that way.
    const nonHighlightEntries = useMemo(() => summaryEntries.filter(([key]) => !getCardStyle(key).isHighlight), [summaryEntries, getCardStyle]);
    const partEntries = useMemo(
        () => nonHighlightEntries.filter(([key, value]) => summaryNumericValue(key, value) > 0 || alwaysShowKey(key)),
        [nonHighlightEntries, alwaysShowKey]
    );

    // The donut is a part-to-whole view of one unit — money. Grand-total fields
    // (isHighlight, or explicitly excludeFromChart — e.g. a running Total that sums
    // other rows in *this* list even though it isn't promoted to a hero tile) are
    // sums of the other fields, so mixing them into the same pie double-counts; a
    // quantity count (nc_qty, sales_qty, ...) mixed in with currency amounts is worse
    // — it inflates the total with numbers that aren't rupees at all, which is what
    // made the category/item summary totals not add up to anything real.
    // Prefer currency-only rows; fall back a step at a time only if there's nothing to chart at all.
    // A single real category (e.g. only Cash in use) is a legitimate one-slice donut — falling back
    // to a broader, unfiltered entry set in that case would reintroduce entries excludeFromChart
    // deliberately dropped (grand totals that already sum the other rows) and double-count the total.
    const chartCandidateEntries = useMemo(() => partEntries.filter(([key]) => !excludeFromChart(key)), [partEntries, excludeFromChart]);
    const currencyPartEntries = useMemo(() => chartCandidateEntries.filter(([key]) => isCurrency(key)), [chartCandidateEntries, isCurrency]);
    const chartEntries =
        currencyPartEntries.length > 0
            ? currencyPartEntries
            : chartCandidateEntries.length > 0
              ? chartCandidateEntries
              : nonHighlightEntries.length > 0
                ? nonHighlightEntries
                : summaryEntries;

    const chartIsCurrency = useMemo(() => chartEntries.some(([key]) => isCurrency(key)), [chartEntries, isCurrency]);

    // Chart.js tracks which slices a legend click has hidden internally (via
    // chart.getDataVisibility), invisible to our React state — so the center "Total"
    // label would otherwise keep summing every slice even after the user hides one.
    // Mirror that hidden-set here so the label recomputes to match what's actually shown.
    const [hiddenSliceIndices, setHiddenSliceIndices] = useState(() => new Set());
    useEffect(() => {
        setHiddenSliceIndices(new Set());
    }, [chartEntries]);

    const chartTotal = useMemo(
        () => chartEntries.reduce((acc, [key, value], idx) => (hiddenSliceIndices.has(idx) ? acc : acc + summaryNumericValue(key, value)), 0),
        [chartEntries, hiddenSliceIndices]
    );

    const chartData = useMemo(() => {
        if (chartEntries.length === 0) return { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
        const labels = chartEntries.map(([key]) => formatLabel(key));
        const values = chartEntries.map(([key, value]) => summaryNumericValue(key, value));
        const background = chartEntries.map(([key]) => extractAccentHex(getCardStyle(key).bg));
        return {
            labels,
            datasets: [{ data: values, backgroundColor: background, borderWidth: 2, borderColor: '#fcfcfb', hoverBorderColor: '#fcfcfb', hoverOffset: 6 }]
        };
    }, [chartEntries, getCardStyle, formatLabel]);

    const hasChartData = chartData.labels.length > 0 && chartData.datasets[0].data.some((v) => v > 0);

    const chartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 1,
            cutout: '62%',
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14, font: { size: 12 }, color: '#52514e' },
                    // Reimplement the default toggle-slice-visibility behavior so we can
                    // also mirror the resulting hidden set into React state for chartTotal.
                    onClick: (_e, legendItem, legend) => {
                        const ci = legend.chart;
                        const index = legendItem.index;
                        ci.toggleDataVisibility(index);
                        ci.update();
                        setHiddenSliceIndices((prev) => {
                            const next = new Set(prev);
                            if (ci.getDataVisibility(index)) next.delete(index);
                            else next.add(index);
                            return next;
                        });
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const v = summaryNumericValue(null, ctx.parsed);
                            const formatted = chartIsCurrency ? `₹${formatCurrency(v)}` : v.toLocaleString('en-IN');
                            return ` ${ctx.label}: ${formatted}`;
                        }
                    }
                }
            }
        }),
        [chartIsCurrency]
    );

    const dateRangeStr =
        fromDate && toDate
            ? `${typeof fromDate === 'object' && fromDate.toLocaleDateString ? fromDate.toLocaleDateString('en-IN') : fromDate} – ${typeof toDate === 'object' && toDate.toLocaleDateString ? toDate.toLocaleDateString('en-IN') : toDate}`
            : '';
    const branchStr = selectedBranch && (selectedBranch.branch_name || selectedBranch) ? ` • ${selectedBranch.branch_name || selectedBranch}` : '';

    const [chartWidthPercent, setChartWidthPercent] = useState(70);
    const summaryCardRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (summaryCardRef.current) {
                const h = summaryCardRef.current.offsetHeight || 0;
                setChartWidthPercent(h > 0 && h < 500 ? 55 : 70);
            }
        }, 0);
        return () => clearTimeout(timer);
    }, [summaryEntries.length, hasChartData]);

    if (summaryEntries.length === 0) return null;

    return (
        <div className="grid mb-4">
            <PrTooltip key={`summary-tooltip-${summaryEntries.length}`} target=".summary-row-tooltip" />
            <div ref={summaryCardRef} className="col-12 lg:col-6">
                <Card className="mb-0 h-full">
                    <div className="mb-3">
                        <div className="flex align-items-center gap-2 flex-nowrap" style={{ minWidth: 0 }}>
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)'
                                }}
                            >
                                <i className="pi pi-chart-line text-white" style={{ fontSize: '1rem' }} />
                            </div>
                            <div className="min-w-0 flex-grow-1" style={{ overflow: 'hidden' }}>
                                <h3 className="text-lg font-bold m-0 truncate" style={{ color: '#1e293b', lineHeight: '1.2' }}>
                                    {title}
                                </h3>
                                <p className="text-xs m-0 mt-0 truncate" style={{ color: '#64748b' }} title={[subtitle || dateRangeStr, dateRangeStr, branchStr.replace(/^ • /, '')].filter(Boolean).join(' • ')}>
                                    {subtitle != null && subtitle !== '' ? subtitle : dateRangeStr}
                                    {dateRangeStr && subtitle != null && subtitle !== '' ? ` • ${dateRangeStr}` : ''}
                                    {branchStr}
                                </p>
                            </div>
                        </div>
                    </div>
                    {highlightEntries.length > 0 && (
                        <div className="grid">
                            {highlightEntries.map(([key, value]) => {
                                const s = getCardStyle(key);
                                const accent = extractAccentHex(s.bg);
                                const displayValue = isCurrency(key) ? `₹${formatCurrency(value || 0)}` : value ?? '-';

                                return (
                                    <div key={key} className="col-12 sm:col-6">
                                        <div
                                            className="border-round-lg transition-all transition-duration-200"
                                            style={{
                                                position: 'relative',
                                                overflow: 'hidden',
                                                minWidth: 0,
                                                background: `linear-gradient(180deg, ${hexToRgba(accent, 0.08)} 0%, #ffffff 65%)`,
                                                border: `1px solid ${hexToRgba(accent, 0.25)}`,
                                                padding: '1.1rem 1.1rem 0.9rem'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = `0 10px 20px -10px ${hexToRgba(accent, 0.45)}`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'none';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: s.bg }} />
                                            <div className="flex align-items-center justify-content-between gap-1 mb-2" style={{ minWidth: 0 }}>
                                                <span
                                                    className="font-semibold text-xs uppercase flex-grow-1"
                                                    style={{ color: '#64748b', letterSpacing: '0.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                                                    title={formatLabel(key)}
                                                >
                                                    {formatLabel(key)}
                                                </span>
                                                <div
                                                    className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                                    style={{ width: '32px', height: '32px', background: hexToRgba(accent, 0.14) }}
                                                >
                                                    <i className={`pi ${s.icon}`} style={{ color: accent, fontSize: '0.85rem' }} />
                                                </div>
                                            </div>
                                            <div className="text-2xl font-bold" style={{ color: '#0b0b0b', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={displayValue}>
                                                {displayValue}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {partEntries.length > 0 && (
                        <div className={highlightEntries.length > 0 ? 'border-round-lg overflow-hidden mt-3' : 'border-round-lg overflow-hidden'} style={{ border: '1px solid rgba(11,11,11,0.08)' }}>
                            {partEntries.map(([key, value], idx) => {
                                const s = getCardStyle(key);
                                const accent = extractAccentHex(s.bg);
                                const emphasized = emphasizeKey(key);
                                const rowTooltip = getTooltip(key);
                                let displayValue;
                                if (isCurrency(key)) {
                                    displayValue = `₹${formatCurrency(value || 0)}`;
                                } else if (typeof value === 'number' && key.toLowerCase().includes('qty')) {
                                    displayValue = value.toLocaleString();
                                } else {
                                    displayValue = value ?? '-';
                                }

                                return (
                                    <div
                                        key={key}
                                        className={`flex align-items-center justify-content-between gap-2${rowTooltip ? ' summary-row-tooltip' : ''}`}
                                        data-pr-tooltip={rowTooltip || undefined}
                                        data-pr-position="top"
                                        style={{
                                            padding: emphasized ? '0.85rem 0.9rem' : '0.65rem 0.9rem',
                                            borderTop: idx === 0 ? 'none' : `1px solid ${emphasized ? hexToRgba(accent, 0.3) : 'rgba(11,11,11,0.06)'}`,
                                            borderLeft: `3px solid ${accent}`,
                                            background: emphasized ? hexToRgba(accent, 0.1) : '#ffffff',
                                            transition: 'background-color 150ms',
                                            cursor: rowTooltip ? 'pointer' : 'default'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = hexToRgba(accent, emphasized ? 0.16 : 0.06);
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = emphasized ? hexToRgba(accent, 0.1) : '#ffffff';
                                        }}
                                    >
                                        <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <div
                                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                                style={{ width: '26px', height: '26px', background: hexToRgba(accent, 0.14) }}
                                            >
                                                <i className={`pi ${s.icon}`} style={{ color: accent, fontSize: '0.7rem' }} />
                                            </div>
                                            <span
                                                className={emphasized ? 'text-sm font-bold uppercase' : 'text-sm font-medium'}
                                                style={{ color: emphasized ? accent : '#374151', letterSpacing: emphasized ? '0.04em' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                title={formatLabel(key)}
                                            >
                                                {formatLabel(key)}
                                            </span>
                                        </div>
                                        <span className="font-bold flex-shrink-0" style={{ color: emphasized ? accent : '#0b0b0b', fontSize: emphasized ? '1.05rem' : '0.875rem' }}>
                                            {displayValue}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
            <div className="col-12 lg:col-6">
                <Card className="h-full">
                    <div className="mb-3">
                        <div className="flex align-items-center gap-2 flex-nowrap" style={{ minWidth: 0 }}>
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.35)'
                                }}
                            >
                                <i className="pi pi-chart-pie text-white" style={{ fontSize: '1rem' }} />
                            </div>
                            <div className="min-w-0 flex-grow-1" style={{ overflow: 'hidden' }}>
                                <h3 className="text-lg font-bold m-0 truncate" style={{ color: '#1e293b', lineHeight: '1.2' }}>
                                    Summary overview
                                </h3>
                                <p className="text-xs m-0 mt-0 truncate" style={{ color: '#64748b' }}>
                                    Breakdown by share{chartEntries.length !== summaryEntries.length ? ' (grand totals excluded)' : ''}
                                </p>
                            </div>
                        </div>
                    </div>
                    {hasChartData ? (
                        <div style={{ position: 'relative', width: `${chartWidthPercent}%`, height: '250px', margin: '0 auto' }}>
                            <Chart type="doughnut" data={chartData} options={chartOptions} />
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                <div style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#898781' }}>Total</div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0b0b0b' }}>{chartIsCurrency ? `₹${formatCurrency(chartTotal)}` : chartTotal.toLocaleString('en-IN')}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex align-items-center justify-content-center text-color-secondary" style={{ minHeight: '200px' }}>
                            <span className="text-sm">No chart data</span>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
